// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import Logger from "@insite/client-framework/Logger";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import {
    canCheckoutWithCart,
    canSubmitRequisition,
    getCurrentCartState,
    hasQuoteRequiredProducts,
    isCartCheckoutDisabled,
    isPunchOutOrder,
} from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import loadCurrentCart from "@insite/client-framework/Store/Data/Carts/Handlers/LoadCurrentCart";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import doPunchOutCheckout from "@insite/client-framework/Store/Pages/Cart/Handlers/DoPunchOutCheckout";
import removeCartLine from "@insite/client-framework/Store/Pages/Cart/Handlers/RemoveCartLine";
import submitRequisition from "@insite/client-framework/Store/Pages/Cart/Handlers/SubmitRequisition";
import updateCartLine from "@insite/client-framework/Store/Pages/Cart/Handlers/UpdateCartLine";
import preloadCheckoutShippingData from "@insite/client-framework/Store/Pages/CheckoutShipping/Handlers/PreloadCheckoutShippingData";
import signInAsGuest from "@insite/client-framework/Store/Pages/SignIn/Handlers/SignInAsGuest";
import translate from "@insite/client-framework/Translate";
import { CartLineModel } from "@insite/client-framework/Types/ApiModels";
import TwoButtonModal from "@insite/content-library/Components/TwoButtonModal";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button/Button";
import LoadingOverlay, { LoadingOverlayProps } from "@insite/mobius/LoadingOverlay/LoadingOverlay";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { HasHistory, useHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import FancyScroll, { FancyScrollRefProps } from "@vanmeter/App/Components/Common/FancyScroll";
import { getCartLineErrorMessages } from "@vanmeter/App/Components/MiniCart/MiniCart.helpers";
import MiniCartLineCard from "@vanmeter/App/Components/MiniCart/MiniCartLineCard";
import Notify from "@vanmeter/App/Components/Notify/Notify";
import xUseSortCartLines from "@vanmeter/App/Hooks/xUseSortCartLines";
import { CustomAppState } from "@vanmeter/App/Store/Reducers";
import { getSettingsCollection } from "@vanmeter/App/Store/Selectors/SettingsSelectors";
import { fontConfig } from "@vanmeter/XDL/constants";
import { color, pluralize, styledScrollbar } from "@vanmeter/XDL/Utilities";
import React, { FC, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    isPopupShown?: boolean;
    onClose?: () => void;
}

const mapStateToProps = (state: CustomAppState) => {
    const cart = getCurrentCartState(state).value;
    const isCheckoutDisabled = isCartCheckoutDisabled(cart);

    return {
        isCheckoutDisabled,
        canSubmitRequisition: canSubmitRequisition(cart),
        canCheckoutWithCart: canCheckoutWithCart(cart),
        isPunchOutOrder: isPunchOutOrder(cart),
        totalCountDisplay: getCurrentCartState(state).value?.totalCountDisplay || 0,
        orderSubTotalDisplay: getCurrentCartState(state).value?.orderSubTotalDisplay,
        cartPageUrl: getPageLinkByPageType(state, "CartPage")?.url,
        requisitionConfirmationPageUrl: getPageLinkByPageType(state, "RequisitionConfirmationPage")?.url,
        checkoutShippingPageUrl: getPageLinkByPageType(state, "CheckoutShippingPage")?.url,
        checkoutReviewAndSubmitPageUrl: getPageLinkByPageType(state, "CheckoutReviewAndSubmitPage")?.url,
        cart,
        isRemovingCartLine: Object.getOwnPropertyNames(state.pages.cart.isRemovingCartLine).length > 0,
        isUpdatingCartLine: state.pages.cart.isUpdatingCartLine,
        isLoading: state.data.carts.isLoading,
        hasQuoteRequiredProducts: hasQuoteRequiredProducts(cart),
        onePageCheckout: getSettingsCollection(state).cartSettings.onePageCheckout,
        onePageCheckoutPageUrl: getPageLinkByPageType(state, "OnePageCheckoutPage")?.url,
        rememberMe: getSession(state).rememberMe,
    };
};

const mapDispatchToProps = {
    loadCurrentCart,
    updateCartLine,
    removeCartLine,
    preloadCheckoutShippingData,
    doPunchOutCheckout,
    submitRequisition,
    signInAsGuest,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

interface MiniCartStyles {
    centerWrapper?: InjectableCss;
    noCartLinesNotify?: TypographyProps;
    minicartFooterContainer?: InjectableCss;
    minicartSummary?: InjectableCss;
    summarySectionWrapper?: InjectableCss;
    messagesWrapper?: InjectableCss;
    cartLinesWrapper?: InjectableCss;
    infoLabel?: TypographyProps;
    infoValue?: TypographyProps;
    checkoutButton?: ButtonPresentationProps;
    viewCartButton?: ButtonPresentationProps;
    loadingOverlay?: LoadingOverlayProps;
    messagesAndCartLinesWrapper?: InjectableCss;
}

export const miniCartStyles: MiniCartStyles = {
    centerWrapper: {
        css: css`
            display: flex;
            justify-content: center;
            align-items: center;
            width: 100%;
            padding: 12px;
        `,
    },
    noCartLinesNotify: {
        css: css`
            margin: 0;
        `,
    },
    loadingOverlay: {
        css: css`
            display: flex;
            flex-direction: column;
        `,
    },
    messagesWrapper: {
        css: css`
            padding: 0 12px;
        `,
    },
    messagesAndCartLinesWrapper: {
        css: css`
            position: relative;
            margin-top: 12px;
            max-height: calc(100vh - var(--headerHeight) - 90px);
            max-height: calc(100svh - var(--headerHeight) - 90px);
            overflow-x: hidden;
            ${styledScrollbar()}
        `,
    },
    cartLinesWrapper: {
        css: css`
            padding: 0 12px;
            margin-top: 12px;

            & > div:not(:last-child) {
                margin-bottom: 12px;
                border-bottom: 1px solid ${color("common.border")};
            }
        `,
    },
    minicartFooterContainer: {
        css: css`
            border-top: 1px solid ${color("common.border")};
            padding: 12px;
            display: flex;
            justify-content: space-between;
            align-items: center;
        `,
    },
    minicartSummary: {
        css: css`
            display: flex;
            justify-content: space-between;
            padding: 12px;
            box-shadow: 0 1px 0 0 ${color("common.border")};
        `,
    },
    infoLabel: {
        ...fontConfig.smallSingleLine,
        css: css`
            margin-right: 4px;
        `,
    },
    infoValue: {
        variant: "h4",
        forwardAs: "span",
        css: css`
            margin-bottom: 0;
            display: inline-block;
        `,
    },
    checkoutButton: {
        variant: "tertiary",
        sizeVariant: "large",
    },
    viewCartButton: {
        buttonType: "outline",
        sizeVariant: "large",
    },
};

const styles = miniCartStyles;

const MiniCart: FC<Props> = ({
    removeCartLine,
    updateCartLine,
    submitRequisition,
    preloadCheckoutShippingData,
    doPunchOutCheckout,
    signInAsGuest,
    cart,
    cartPageUrl,
    requisitionConfirmationPageUrl,
    isPunchOutOrder,
    isCheckoutDisabled,
    totalCountDisplay,
    orderSubTotalDisplay,
    canSubmitRequisition,
    isUpdatingCartLine,
    isRemovingCartLine,
    isLoading,
    checkoutShippingPageUrl,
    checkoutReviewAndSubmitPageUrl,
    onePageCheckoutPageUrl,
    isPopupShown,
    hasQuoteRequiredProducts,
    onePageCheckout,
    rememberMe,
    onClose,
}) => {
    const history = useHistory();
    const [isCheckoutLoading, setIsCheckoutLoading] = useState(false);
    const parentBoxRef = React.createRef<HTMLDivElement>();
    const fancyScrollRef = React.createRef<FancyScrollRefProps>();
    const [quoteRequiredModalIsOpen, setQuoteRequiredModalIsOpen] = useState(false);

    useEffect(() => {
        if (isPopupShown && fancyScrollRef.current) {
            fancyScrollRef.current.updateScrollable();
        }
    }, [isPopupShown]);

    const qtyOrderedChangeHandler = (cartLine: CartLineModel, qtyOrdered: number) => {
        if (qtyOrdered <= 0) {
            removeCartLine({ cartLineId: cartLine.id });
        } else {
            updateCartLine({
                cartLine: {
                    ...cartLine,
                    qtyOrdered,
                },
            });
        }
    };

    const checkoutHandler = () => {
        if (!cart) {
            return;
        }
        if (isPunchOutOrder) {
            doPunchOutCheckout();
            return;
        }
        if (!quoteRequiredModalIsOpen && hasQuoteRequiredProducts) {
            setQuoteRequiredModalIsOpen(true);
            return;
        }

        setQuoteRequiredModalIsOpen(false);
        setIsCheckoutLoading(true);

        const { isAuthenticated, isAwaitingApproval, id: cartId, canBypassCheckoutAddress } = cart;
        if (onePageCheckout && onePageCheckoutPageUrl) {
            if (!isAuthenticated && !rememberMe) {
                signInAsGuest({ returnUrl: onePageCheckoutPageUrl });
            } else {
                history.push(
                    isAwaitingApproval ? `${onePageCheckoutPageUrl}?cartId=${cartId}` : onePageCheckoutPageUrl,
                );
            }
            return;
        }

        if (canBypassCheckoutAddress) {
            history.push(checkoutReviewAndSubmitPageUrl!);
            return;
        }

        preloadCheckoutShippingData({
            cartId: isAwaitingApproval ? cartId : undefined,
            onSuccess: () => {
                history.push(
                    isAwaitingApproval ? `${checkoutShippingPageUrl}?cartId=${cartId}` : checkoutShippingPageUrl!,
                );
            },
        });
    };

    const submitRequisitionClickHandler = () => {
        submitRequisition({
            onSuccess: ({ id }: { id: string }) => {
                if (requisitionConfirmationPageUrl) {
                    history.push(`${requisitionConfirmationPageUrl}?cartId=${id}`);
                }
            },
        });
    };

    const removeCartLineClickHandler = (cartLine: CartLineModel) => {
        removeCartLine({ cartLineId: cartLine.id });
    };

    const handleViewCarClick = () => {
        if (!cartPageUrl) {
            Logger.error(`No Cart Page Url found`);
            return;
        }
        history.push(cartPageUrl);
        onClose?.();
    };

    // <XNG-Changes>: VMS-1115 sort cart items to show items with errors first
    const cartLines = xUseSortCartLines({ cart });
    // </XNG-Changes>

    if (!cart) {
        return (
            <StyledWrapper {...styles.centerWrapper}>
                <LoadingSpinner />
            </StyledWrapper>
        );
    }

    const isLoadingCart = isLoading[cart.id];
    const isCheckoutButtonDisabled = isCheckoutDisabled || isCheckoutLoading || isLoadingCart;

    return (
        <>
            <LoadingOverlay
                {...styles.loadingOverlay}
                loading={isLoading[cart.id] || isUpdatingCartLine || isRemovingCartLine}
            >
                <StyledWrapper {...styles.minicartSummary}>
                    <StyledWrapper {...styles.summarySectionWrapper}>
                        <Typography {...styles.infoLabel}>
                            {pluralize(totalCountDisplay, translate("Item"))}:
                        </Typography>
                        <Typography {...styles.infoValue}>{totalCountDisplay}</Typography>
                    </StyledWrapper>
                    <StyledWrapper {...styles.summarySectionWrapper}>
                        <Typography {...styles.infoLabel}>{translate("Subtotal")}:</Typography>
                        <Typography {...styles.infoValue}>{orderSubTotalDisplay}</Typography>
                    </StyledWrapper>
                </StyledWrapper>

                {cartLines.length > 0 ? (
                    <FancyScroll refElement={parentBoxRef} ref={fancyScrollRef}>
                        <StyledWrapper {...styles.messagesAndCartLinesWrapper} ref={parentBoxRef}>
                            <StyledWrapper {...styles.messagesWrapper}>
                                {createWidgetElement("Common/CartApproverMessage", { fields: {} })}
                            </StyledWrapper>
                            <StyledWrapper {...styles.cartLinesWrapper} data-test-selector="cartLines-container">
                                {cartLines.map(cartLine => {
                                    const errorMessages = getCartLineErrorMessages(cartLines, cartLine);
                                    return (
                                        <MiniCartLineCard
                                            key={`miniCartLine_${cartLine.id}`}
                                            cartLine={cartLine}
                                            cart={cart}
                                            qtyOrderedChangeHandler={qtyOrderedChangeHandler}
                                            removeCartLineClickHandler={removeCartLineClickHandler}
                                            errorMessages={errorMessages}
                                        />
                                    );
                                })}
                            </StyledWrapper>
                        </StyledWrapper>
                    </FancyScroll>
                ) : (
                    <StyledWrapper {...styles.centerWrapper}>
                        <Notify {...styles.noCartLinesNotify} type="danger">
                            {siteMessage("Cart_NoOrderLines")}
                        </Notify>
                    </StyledWrapper>
                )}
                {cartLines.length > 0 && (
                    <StyledWrapper {...styles.minicartFooterContainer} data-test-selector="miniCart-footer-container">
                        <Button
                            {...styles.viewCartButton}
                            aria-label={translate("View Cart")}
                            onClick={handleViewCarClick}
                        >
                            {translate("View Cart")}
                        </Button>
                        {canSubmitRequisition ? (
                            <Button
                                {...styles.checkoutButton}
                                onClick={submitRequisitionClickHandler}
                                aria-label={translate("Submit Requisition")}
                            >
                                {translate("Submit Requisition")}
                            </Button>
                        ) : (
                            <>
                                <Button
                                    {...styles.checkoutButton}
                                    onClick={checkoutHandler}
                                    disabled={isCheckoutButtonDisabled}
                                    aria-label={
                                        cart?.requiresApproval
                                            ? translate("Checkout for Approval")
                                            : translate("Continue to Checkout")
                                    }
                                >
                                    {cart?.requiresApproval
                                        ? translate("Checkout for Approval")
                                        : translate("Continue to Checkout")}
                                </Button>
                                <TwoButtonModal
                                    headlineText=""
                                    modalIsOpen={quoteRequiredModalIsOpen}
                                    messageText={siteMessage("OrderApproval_RequiresQuoteMessage")}
                                    cancelButtonText={translate("Cancel")}
                                    submitButtonText={translate("Continue")}
                                    onCancel={() => setQuoteRequiredModalIsOpen(false)}
                                    onSubmit={checkoutHandler}
                                    submitTestSelector="QuoteInCartMessageSubmit"
                                    dataTestSelector="twoButtonModal_RequiresQuote"
                                ></TwoButtonModal>
                            </>
                        )}
                    </StyledWrapper>
                )}
            </LoadingOverlay>
        </>
    );
};

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
export default connect(mapStateToProps, mapDispatchToProps)(withHistory(MiniCart));
